import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";

import { SkeletonPreviewPlaceholder } from "./SkeletonPreviewPlaceholder";

interface CustomStylesErrorCardProps {
  isSelected: boolean;
  onSelect: () => void;
  onRetry: (e: React.MouseEvent) => void;
}

export function CustomStylesErrorCard({
  isSelected,
  onSelect,
  onRetry,
}: CustomStylesErrorCardProps) {
  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      aria-label="Custom styles error"
      className={twMerge(
        "border border-rose-border flex flex-col gap-0 rounded-xl p-0 shadow-md overflow-hidden cursor-pointer w-full md:w-75 shrink-0 h-89 transition-all duration-200",
        isSelected && "border-[0.8px] border-black",
      )}
    >
      <div className="flex flex-col gap-0 p-0 flex-1 overflow-hidden relative bg-rose-soft">
        <SkeletonPreviewPlaceholder />

        <div className="absolute top-4 left-3 bg-white border-0.5 border-rose-border p-3 rounded-xl shadow-2xs">
          <div className="w-48 h-10 rounded-xl bg-rose-soft" />
        </div>

        <div className="absolute top-24 -right-6 bg-white border-0.5 border-rose-border p-3 rounded-lg shadow-2xs">
          <div className="flex gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-2xs font-medium text-placeholder">
                Headings
              </span>
              <div className="w-24 h-3.5 rounded-xl bg-rose-soft" />
            </div>
            <div className="flex flex-col gap-1 w-23">
              <span className="text-2xs font-medium text-placeholder">
                Body
              </span>
              <div className="w-24 h-3.5 rounded-xl bg-rose-soft" />
            </div>
          </div>
        </div>

        <div className="absolute top-44 left-3 bg-white border-0.5 border-rose-border p-3 rounded-xl shadow-2xs">
          <div className="w-17 h-5 rounded-xl bg-rose-soft" />
        </div>

        <div className="absolute bottom-0 inset-x-0 h-17 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </div>

      <div className="flex flex-col items-start gap-3 p-4 bg-white relative z-10">
        <div className="flex flex-col gap-0.5">
          <span className="text-base font-semibold text-default">
            We couldn't pull your styles
          </span>
          <span className="text-sm text-muted">
            We can't access this URL right now. Please try a different one.
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onRetry(e);
          }}
          className="w-full"
        >
          Try a Different URL
        </Button>
      </div>
    </div>
  );
}
